import React from 'react';
import { ArrowRight, Zap, Truck, MapPin, Calendar as CalIcon } from 'lucide-react';
import { useAudience } from '@/context/AudienceContext';
import { TID } from '@/testIds';
import AudienceNudge from './AudienceNudge';

const B2B = {
  eyebrow: 'B2B WHITE LABEL INSTALLATION',
  headlineTop: 'THE UNBRANDED',
  headlineMid: 'INSTALL CREW',
  headlineBot: 'BEHIND YOUR SHOP.',
  body: (
    <>
      Motor iNKS is the seamless, <span className="text-white">blind-shipping fulfillment team</span> for print production
      partners, sign shops and agencies. Drop-ship your printed panels, we install to master-level standard, your invoice
      goes to your client. Zero brand contamination. <span className="text-white">Zero-failure guarantee.</span>
    </>
  ),
  primaryCta: 'Apply as Partner',
  primaryHref: '/partners',
  secondaryCta: 'View wholesale rates',
  secondaryHref: '/rates',
  stats: [
    { k: '15+', v: 'YRS INSTALL' },
    { k: '$2.50', v: 'SQ FT · FLAT' },
    { k: '48H', v: 'AVG TURN' },
  ],
  tag: { icon: Truck, label: 'BLIND-SHIP FULFILLMENT' },
};

const RETAIL = {
  eyebrow: 'AUTOMOTIVE + ARCHITECTURAL WRAPS',
  headlineTop: 'WRAP IT.',
  headlineMid: 'INSTALL IT.',
  headlineBot: 'OWN THE STREET.',
  body: (
    <>
      Certified installers, premium cast media, and the tightest turnaround in Houston. Cars, trucks, fleets, walls,
      floors, windows — anywhere vinyl belongs.
    </>
  ),
  primaryCta: 'Get a Quote',
  primaryHref: '#quote',
  secondaryCta: 'Try the Wrapulator',
  secondaryHref: '#wrapulator',
  stats: [
    { k: '15+', v: 'YRS INSTALL' },
    { k: '1.2M', v: 'SQFT WRAPPED' },
    { k: '48H', v: 'AVG TURN' },
  ],
  tag: { icon: Zap, label: 'CERTIFIED INSTALLERS' },
};

export default function Hero() {
  const { isB2B } = useAudience();
  const c = isB2B ? B2B : RETAIL;
  const Tag = c.tag.icon;
  return (
    <section id="top" className="relative min-h-[100svh] flex items-end lg:items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: isB2B
            ? "url('https://images.pexels.com/photos/10126663/pexels-photo-10126663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800')"
            : "url('https://images.unsplash.com/photo-1535448580089-c7f9490c78b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxzbGVlayUyMHNwb3J0cyUyMGNhcnxlbnwwfHx8fDE3ODU4MDA2MjZ8MA&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
      <div className="absolute inset-0 mi-grid-lines opacity-40" />

      <div className="relative max-w-[1400px] mx-auto w-full px-6 lg:px-10 pt-32 pb-16 lg:py-24">
        <div className="max-w-3xl">
          <AudienceNudge />
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="mi-tag mi-tag-red"><Tag className="w-3 h-3" /> {c.tag.label}</span>
            <span className="mi-tag">{c.eyebrow}</span>
            <span className="mi-tag">EST · 15+ YRS</span>
          </div>

          <h1 className="mi-headline text-white text-[13vw] leading-[0.88] md:text-8xl lg:text-[7.5rem]">
            {c.headlineTop}<br />
            <span className="text-[#E63946]">{c.headlineMid}</span><br />
            {c.headlineBot}
          </h1>

          <p className="mt-8 max-w-2xl text-lg text-white/75 leading-relaxed">{c.body}</p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href={c.primaryHref} data-testid={TID.heroPrimaryCta} className="mi-btn-red">
              {c.primaryCta} <ArrowRight className="w-4 h-4" />
            </a>
            <a href={c.secondaryHref} data-testid={TID.heroSecondaryCta} className="mi-btn-ghost">
              {c.secondaryCta}
            </a>
            {!isB2B && (
              <>
                <a href="/schedule" data-testid="hero-book-now" className="mi-btn-ghost">
                  <CalIcon className="w-4 h-4" /> Book Now
                </a>
                <a href="#installer-guild" data-testid="hero-find-installer" className="mi-btn-ghost">
                  <MapPin className="w-4 h-4" /> Find An Installer
                </a>
              </>
            )}
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
            {c.stats.map((s) => (
              <div key={s.v} className="border-l mi-line-strong pl-4">
                <div className="mi-headline text-3xl md:text-4xl text-white">{s.k}</div>
                <div className="mi-eyebrow mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 right-6 mi-eyebrow text-white/40 hidden md:block">
        HOUSTON · TX · USA · SHIPS NATIONWIDE
      </div>
    </section>
  );
}
